import React, { useRef } from "react";
import { Grid, Paper, Typography, Box } from "@mui/material";
import { Doughnut, Bar } from "react-chartjs-2";
import GradeIcon from "@mui/icons-material/Grade";

const GradeDistribution = ({ gradeStats, onBarClick, onGradeClick }) => {
  const gradeChartRef = useRef(null);

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={2}
          sx={{ p: 3, height: "100%", borderRadius: "12px" }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "#2185d0" }}
          >
            Grade Distribution
          </Typography>
          <div style={{ height: 250 }}>
            <Doughnut
              data={gradeStats.chartData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "right",
                    labels: {
                      boxWidth: 12,
                      font: {
                        size: 10,
                      },
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const label = context.label || "";
                        const value = context.raw || 0;
                        const total =
                          context.chart.data.datasets[0].data.reduce(
                            (a, b) => a + b,
                            0
                          );
                        const percentage = Math.round((value / total) * 100);
                        const displayLabel =
                          label === "Unassigned" ? label : `Grade ${label}`;
                        return `${displayLabel}: ${value} (${percentage}%)`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper
          elevation={2}
          sx={{ p: 3, height: "100%", borderRadius: "12px" }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "#2185d0" }}
          >
            Staff by Grade (Click bars to view details)
          </Typography>
          <div style={{ height: 250 }}>
            <Bar
              ref={gradeChartRef}
              data={gradeStats.barChartData}
              options={{
                maintainAspectRatio: false,
                onClick: onBarClick,
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0,
                    },
                  },
                  x: {
                    ticks: {
                      font: {
                        size: 10,
                      },
                      maxRotation: 45,
                      minRotation: 45,
                      callback: function (value, index) {
                        const grade = this.getLabelForValue(value);
                        return grade;
                      },
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      title: function (tooltipItems) {
                        const grade = tooltipItems[0].label;
                        return grade === "Unassigned" ? grade : `Grade ${grade}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </Paper>
      </Grid>

      {/* Grade Legend */}
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="subtitle2" sx={{ mr: 2 }}>
            Grade Level:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: "#3680e5",
                borderRadius: 1,
                mr: 1,
              }}
            ></Box>
            <Typography variant="body2" sx={{ mr: 3 }}>
              Lower (G1-G3)
            </Typography>

            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: "#3CB371",
                borderRadius: 1,
                mr: 1,
              }}
            ></Box>
            <Typography variant="body2" sx={{ mr: 3 }}>
              Middle (G4-G6)
            </Typography>

            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: "#FFA500",
                borderRadius: 1,
                mr: 1,
              }}
            ></Box>
            <Typography variant="body2" sx={{ mr: 3 }}>
              Upper (G7-G9)
            </Typography>

            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: "#FF8C00",
                borderRadius: 1,
                mr: 1,
              }}
            ></Box>
            <Typography variant="body2">Highest (G10)</Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Grade Count Boxes */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: "12px",
                backgroundColor: "rgba(54, 162, 235, 0.9)",
                color: "white",
              }}
            >
              <GradeIcon sx={{ fontSize: "2.5rem", mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {gradeStats.gradedStaffCount}
              </Typography>
              <Typography variant="body2">Staff with Grades</Typography>
            </Paper>
          </Grid>

          {["G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8", "G9", "G10"].map(
            (gradeId) => (
              <Grid item xs={6} sm={4} md={2} key={gradeId}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: "12px",
                    border: "1px solid #eee",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    ...(gradeStats.counts[gradeId] > 0
                      ? {
                          borderColor: gradeStats.getGradeColor(gradeId),
                          backgroundColor: `${gradeStats.getGradeColor(
                            gradeId
                          )}15`,
                        }
                      : {}),
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={() => {
                    if (gradeStats.counts[gradeId] > 0) {
                      onGradeClick(gradeId);
                    }
                  }}
                >
                  <GradeIcon
                    sx={{
                      fontSize: "2rem",
                      mb: 1,
                      color: gradeStats.getGradeColor(gradeId),
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {gradeStats.counts[gradeId] || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: gradeStats.counts[gradeId] > 0 ? 600 : 400,
                      color:
                        gradeStats.counts[gradeId] > 0
                          ? gradeStats.getGradeColor(gradeId)
                          : "text.secondary",
                    }}
                  >
                    Grade {gradeId}
                  </Typography>
                </Paper>
              </Grid>
            )
          )}

          {/* Unassigned Grade Card */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderRadius: "12px",
                border: "1px solid #eee",
                cursor: "pointer",
                backgroundColor: "rgba(199, 199, 199, 0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                },
              }}
              onClick={() => {
                if (gradeStats.counts["Unassigned"] > 0) {
                  onGradeClick("Unassigned");
                }
              }}
            >
              <GradeIcon sx={{ fontSize: "2rem", mb: 1, color: "#777" }} />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {gradeStats.counts["Unassigned"] || 0}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                Unassigned
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default GradeDistribution;
